import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { AlertCircle, CalendarClock, CalendarDays, CheckCircle2 } from 'lucide-react';
import { DateRangeSelect, useHubData, fmtUSD, rangeQuery, CardError, CardEmpty } from './DateRangeSelect';

const DONUT_COLORS = ['#2979F2', '#20B94B', '#7657F6', '#14B8A6', '#EF4444'];

export function ProfitLossSummary({ wsId, range, onRangeChange }) {
  const navigate = useNavigate();
  const url = wsId ? `/api/dashboard-hub/profit-loss?workspace_id=${wsId}&${rangeQuery(range)}` : null;
  const { data, loading, error, retry } = useHubData(url);
  const goReport = () => navigate(`/reports?report=profit-loss&preset=${range.preset}${range.startDate ? `&start=${range.startDate}&end=${range.endDate}` : ''}`);

  const rows = data ? [
    { label: 'Revenue', value: data.revenue, cls: 'text-[#118A35]' },
    { label: 'Cost of Goods Sold', value: -Math.abs(data.cogs), cls: 'text-[#EF4444]' },
    { label: 'Gross Profit', value: data.gross_profit, cls: data.gross_profit >= 0 ? 'text-[#172033]' : 'text-[#EF4444]', bold: true },
    { label: 'Operating Expenses', value: -Math.abs(data.opex), cls: 'text-[#EF4444]' },
    { label: 'Net Profit', value: data.net_profit, cls: data.net_profit >= 0 ? 'text-[#118A35]' : 'text-[#EF4444]', bold: true },
  ] : [];

  return (
    <section className="rounded-2xl border border-[#E5EAF0] bg-white p-5 shadow-sm" data-testid="pnl-card" aria-label="Profit and loss summary">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-[#172033]">Profit &amp; Loss Summary</h2>
        <DateRangeSelect value={range} onChange={onRangeChange} ariaLabel="Date range for profit and loss" />
      </div>
      {loading ? (
        <div className="mt-4 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-8 animate-pulse rounded-lg bg-slate-100" />)}</div>
      ) : error ? (
        <CardError onRetry={retry} />
      ) : !data?.has_data ? (
        <CardEmpty />
      ) : (
        <div className="mt-3 divide-y divide-[#EEF2F6]">
          {rows.map((row) => (
            <button
              key={row.label} type="button" onClick={goReport}
              data-testid={`pnl-row-${row.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              className="flex w-full items-center justify-between px-1 py-2.5 text-left transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20B94B]/40"
            >
              <span className={`text-sm ${row.bold ? 'font-bold text-[#172033]' : 'text-[#657085]'}`}>{row.label}</span>
              <span className={`text-sm font-semibold ${row.cls}`}>{row.value < 0 ? `-${fmtUSD(Math.abs(row.value))}` : fmtUSD(row.value)}</span>
            </button>
          ))}
          <div className="flex items-center justify-between px-1 py-2.5">
            <span className="text-sm text-[#657085]">Net Profit Margin</span>
            <span className="text-sm font-semibold text-[#172033]" data-testid="pnl-margin">
              {data.margin === null || data.margin === undefined ? '—' : `${data.margin.toFixed(1)}%`}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

export function TopExpenses({ wsId, range, onRangeChange }) {
  const navigate = useNavigate();
  const url = wsId ? `/api/dashboard-hub/expenses?workspace_id=${wsId}&${rangeQuery(range)}` : null;
  const { data, loading, error, retry } = useHubData(url);

  return (
    <section className="rounded-2xl border border-[#E5EAF0] bg-white p-5 shadow-sm" data-testid="top-expenses-card" aria-label="Top expenses">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-[#172033]">Top Expenses</h2>
        <DateRangeSelect value={range} onChange={onRangeChange} ariaLabel="Date range for top expenses" />
      </div>
      {loading ? (
        <div className="mt-4 h-52 animate-pulse rounded-xl bg-slate-100" />
      ) : error ? (
        <CardError onRetry={retry} />
      ) : !data?.has_data ? (
        <CardEmpty />
      ) : (
        <div className="mt-2 flex flex-col items-center gap-4 sm:flex-row">
          <div className="relative h-48 w-48 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.categories} dataKey="amount" nameKey="name"
                  innerRadius={58} outerRadius={82} paddingAngle={2} strokeWidth={0}>
                  {data.categories.map((c, i) => <Cell key={c.name} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [fmtUSD(v), n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-lg font-bold text-[#172033]" data-testid="expenses-total">{fmtUSD(data.total)}</p>
              <p className="text-[11px] text-[#657085]">Total Expenses</p>
            </div>
          </div>
          <ul className="w-full min-w-0 space-y-1">
            {data.categories.map((c, i) => (
              <li key={c.name}>
                <button
                  type="button"
                  data-testid={`expense-cat-${i}`}
                  onClick={() => navigate(c.account_id ? `/reports?report=profit-loss&account=${c.account_id}` : '/reports?report=profit-loss')}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20B94B]/40"
                >
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                  <span className="min-w-0 flex-1 truncate text-sm text-[#172033]">{c.name}</span>
                  <span className="text-sm font-semibold text-[#172033]">{fmtUSD(c.amount)}</span>
                  <span className="w-10 text-right text-xs text-[#657085]">{c.pct}%</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

const INVOICE_ROWS = [
  { key: 'overdue', label: 'Overdue', icon: AlertCircle, cls: 'text-[#EF4444]', bg: 'bg-[#EF4444]/10', filter: 'overdue' },
  { key: 'due_this_week', label: 'Due This Week', icon: CalendarClock, cls: 'text-[#FF7816]', bg: 'bg-[#FF7816]/10', filter: 'due_soon' },
  { key: 'due_this_month', label: 'Due This Month', icon: CalendarDays, cls: 'text-[#2979F2]', bg: 'bg-[#2979F2]/10', filter: 'open' },
  { key: 'paid_in_period', label: 'Paid During Period', icon: CheckCircle2, cls: 'text-[#118A35]', bg: 'bg-[#20B94B]/10', filter: 'paid' },
];

export function InvoiceOverview({ wsId, range, onRangeChange }) {
  const navigate = useNavigate();
  const url = wsId ? `/api/dashboard-hub/invoices-overview?workspace_id=${wsId}&${rangeQuery(range)}` : null;
  const { data, loading, error, retry } = useHubData(url);

  return (
    <section className="rounded-2xl border border-[#E5EAF0] bg-white p-5 shadow-sm" data-testid="invoices-overview-card" aria-label="Invoices overview">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-[#172033]">Invoices Overview</h2>
        <DateRangeSelect value={range} onChange={onRangeChange} ariaLabel="Date range for invoices overview" />
      </div>
      {loading ? (
        <div className="mt-4 space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />)}</div>
      ) : error ? (
        <CardError onRetry={retry} />
      ) : !data?.has_data ? (
        <CardEmpty label="No invoices yet — create your first invoice to see this overview." />
      ) : (
        <div className="mt-3 divide-y divide-[#EEF2F6]">
          {INVOICE_ROWS.map((row) => {
            const Icon = row.icon;
            const bucket = data[row.key] || { total: 0, count: 0 };
            return (
              <button
                key={row.key} type="button"
                data-testid={`invoice-row-${row.key}`}
                onClick={() => navigate(`/invoices?filter=${row.filter}`)}
                className="flex w-full items-center gap-3 px-1 py-2.5 text-left transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20B94B]/40"
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${row.bg}`}>
                  <Icon className={`h-4 w-4 ${row.cls}`} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-[#172033]">{row.label}</span>
                <span className={`text-sm font-bold ${row.key === 'overdue' && bucket.total > 0 ? 'text-[#EF4444]' : 'text-[#172033]'}`}>
                  {fmtUSD(bucket.total)}
                </span>
                <span className="w-20 text-right text-xs text-[#657085]">{bucket.count} invoice{bucket.count === 1 ? '' : 's'}</span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
